


























import { CreateElement, VNode } from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { SwExtendedVue } from '../sw-extended-vue';
import { popover, PopoverDirValues } from '../popover-dir';
import { SwTextfield } from '../sw-textfield';
import SwDatePicker from './sw-date-picker.vue';
import SwTimePicker from './sw-time-picker.vue';
import { DateTimePickerConfig } from './types';
import utilService from './util.service';

const cssPrefix = 'sw-datetime-picker';
const i18nPathPrefix = 'libShared.SwDatetimePicker';

@Component({
  components: {
    SwTextfield,
    SwDatePicker,
    SwTimePicker,
  },
  directives: {
    popover,
  },
})
export default class SwDatetimePicker extends SwExtendedVue {
  // considering accept [date, date]|string
  @Prop(String)
  value: string;

  @Prop({ type: Object, default() { return { type: 'date', dateFormat: 'MM/DD/YYYY', pane: 1, enableRange: false } as DateTimePickerConfig; } })
  pickerConfig: DateTimePickerConfig;

  @Prop({ type: Boolean, default: false })
  full: boolean;

  @Prop({ type: Boolean, default: false })
  disabled: boolean;

  @Prop({ default: 'normal' })
  type: 'normal'|'readonly';

  cssPrefix = cssPrefix;
  i18nPathPrefix = i18nPathPrefix;

  stagingDateTimeValue: string = '';
  dateValue = '';
  timeValue = '';
  rangeSplitter = '->';
  textfieldFocus: boolean = false;
  outsidePopover: boolean = true;

  get isStagingDateTimeValueValid() {
    return utilService.isDatetimeStrValid(this.stagingDateTimeValue, this.pickerConfig, this.rangeSplitter);
  }

  get isClickPopoverVisible() {
    if (this.disabled) { return false; }
    return this.textfieldFocus || !this.outsidePopover;
  }

  get clickPopoverValue(): PopoverDirValues {
    return {
      title: '',
      /* tslint:disable-next-line */
      content: (h:CreateElement):VNode => {
        let rootNode: VNode | null = null;
        const dateNode: VNode = (<SwDatePicker
          props-value={this.dateTimeObj.date}
          props-format={this.pickerConfig.dateFormat}
          props-pane={this.pickerConfig.pane}
          props-enableRange={this.pickerConfig.enableRange}
          props-onDayClick={this.pickerConfig.onDayClick}
          props-disabledDaysOfWeek={this.pickerConfig.disabledDaysOfWeek}
          props-selectableDatesRange={this.pickerConfig.selectableDatesRange}
          props-hidePreAndNextMonthDates={this.pickerConfig.hidePreAndNextMonthDates}
          on-input={(newVal:string) => { this.dateValue = newVal; } }></SwDatePicker>) as VNode;
        const timeNode: VNode = (<SwTimePicker
          props-value={this.dateTimeObj.time}
          props-pane={this.pickerConfig.pane}
          props-enableRange={this.pickerConfig.enableRange}
          props-disableSec={this.pickerConfig.disableSec}
          props-disableMin={this.pickerConfig.disableMin}
          on-input={(newVal:string) => { this.timeValue = newVal; } }></SwTimePicker>) as VNode;
        // default: date-picker mode.
        rootNode = (
          <div>{dateNode}</div>
        ) as VNode;
        if (this.pickerConfig.type === 'time') {
          rootNode = (
            <div>{timeNode}</div>
          ) as VNode;
        }
        if (this.pickerConfig.type === 'datetime') {
          rootNode = (
            <div on-click={this.onClickPopover}>
              {dateNode}
              {timeNode}
            </div>
          ) as VNode;
        }
        return rootNode;
      },
      withCloseIcon: false,
      noPadding: true,
      maxHeight: 500,
      trigger: 'controlled',
      visible: this.isClickPopoverVisible,
      // onRequestClose: () => (this.isClickPopoverVisible = false),
      onClickOutsidePopper: () => { this.outsidePopover = true; },
    };
  }

  get getPlaceHolder() {
    if (this.pickerConfig.placeholder) {
      return this.pickerConfig.placeholder;
    }
    return this.pickerConfig.enableRange ? this.$t(`${i18nPathPrefix}.selectDateTimeRange`) : this.$t(`${i18nPathPrefix}.selectDateTime`);
  }

  /** compose string typed value. can be datetime or datetime->datetime */
  get dateTimeStr() {
    const dates = this.dateValue.split(this.rangeSplitter);
    const times = this.timeValue.split(this.rangeSplitter);
    if (this.pickerConfig.enableRange) {
      // if type === date, times[1] undefined, type === time, dates[1] undefined
      if (this.pickerConfig.type === 'time') {
        return `${times[0]}${this.rangeSplitter}${times[1]}`;
      } if (this.pickerConfig.type === 'datetime') {
        return `${dates[0]} ${times[0]}${this.rangeSplitter}${dates[1]} ${times[1]}`;
      }
      return `${dates[0]}${this.rangeSplitter}${dates[1]}`;
    }
    return this.pickerConfig.type === 'time' ? times[0] : this.pickerConfig.type === 'datetime' ?
      `${dates[0]} ${times[0]}` : dates[0];
  }

  /** parse input value to date and time
   *  if string. it should be in format of 'date time->date time'.
   */
  get dateTimeObj() {
    if (typeof this.value === 'string') {
      const times: string[] = [];
      const dates: string[] = [];
      const datetimeRange = this.value.trim()
        .split(this.rangeSplitter);
      if (this.pickerConfig.type === 'time') {
        datetimeRange.forEach((ele) => {
          times.push(ele);
        });
        return {
          date: '',
          time: times.join(this.rangeSplitter),
        };
      } if (this.pickerConfig.type === 'datetime') {
        datetimeRange.forEach((ele) => {
          const datetimeArr = ele.split(' ');
          dates.push(datetimeArr[0]);
          times.push(datetimeArr[1]);
        });
        return {
          date: dates.join(this.rangeSplitter),
          time: times.join(this.rangeSplitter),
        };
      }
      datetimeRange.forEach((ele) => {
        dates.push(ele);
      });
      return {
        date: dates.join(this.rangeSplitter),
        time: '',
      };
    }
    return this.value;
  }

  @Watch('value', { immediate: true })
  private onValueChange() {
    this.stagingDateTimeValue = this.value;
  }

  @Watch('dateTimeObj')
  private updateDateTime() {
    this.dateValue = this.dateTimeObj.date;
    this.timeValue = this.dateTimeObj.time;
  }

  @Watch('dateTimeStr')
  private onOutputDateTimeStrChange() {
    this.$emit('input', this.dateTimeStr);
  }

  private onFocus() {
    this.textfieldFocus = true;
    this.outsidePopover = false;
  }

  private onBlur(event: FocusEvent) {
    this.textfieldFocus = false;
    if (event.relatedTarget) {
      /** this happens when tab away */
      this.outsidePopover = true;
    }
    this.emitStaging();
  }

  private onEnter() {
    this.emitStaging();
  }

  private onClickPopover() {
    const textfield = this.$refs.inputTextfield as SwTextfield;
    if (textfield) { textfield.focus(); }
  }

  private emitStaging() {
    if (!this.isStagingDateTimeValueValid) { return; }
    this.$emit('input', this.stagingDateTimeValue);
  }

  private created() {
    this.updateDateTime();
  }
}
